import React, { useState, useEffect } from 'react';
import SystemManagement from '../components/admin/SystemManagement';
import AuditLog from '../components/admin/AuditLog';
import SecurityDashboard from '../components/admin/SecurityDashboard';
import AnnouncementsManagement from '../components/admin/AnnouncementsManagement';
import SupportTickets from "../components/SupportTickets";
import KnowledgeBase from '../components/KnowledgeBase';
import SettingsModal from '../components/SettingsModal';

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  active: { bg: '#d1fae5', color: '#065f46', label: 'Active' },
  trial: { bg: '#e0f2fe', color: '#0369a1', label: 'Trial' },
  trialing: { bg: '#e0f2fe', color: '#0369a1', label: 'Trial' },
  past_due: { bg: '#fef3c7', color: '#92400e', label: 'Past Due' },
  canceled: { bg: '#fee2e2', color: '#991b1b', label: 'Canceled' },
  incomplete: { bg: '#f1f5f9', color: '#475569', label: 'Incomplete' },
  incomplete_expired: { bg: '#f1f5f9', color: '#475569', label: 'Expired' },
  paused: { bg: '#f5f3ff', color: '#5b21b6', label: 'Paused' },
  Free: { bg: '#f0fdf4', color: '#059669', label: 'Free' },
  Internal: { bg: '#f3e8ff', color: '#6b21a8', label: 'Internal' }
};

interface AdminPortalProps {
  token: string;
  onLogout: () => void;
}

const TAB_STYLE = (active: boolean) => ({
  padding: '8px 18px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer' as const,
  fontWeight: (active ? '600' : '500') as string,
  fontSize: '13px',
  background: active ? 'linear-gradient(135deg, #1e3c72, #2a5298)' : 'transparent',
  color: active ? 'white' : '#94a3b8',
  transition: 'all 0.2s',
  whiteSpace: 'nowrap' as const
});

const CARD_STYLE = {
  background: 'white',
  padding: '24px',
  borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.2s'
};

const MODAL_OVERLAY: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', zIndex: 1000, padding: '16px'
};

const MODAL_CARD: React.CSSProperties = {
  background: 'white', borderRadius: '16px', padding: '28px',
  width: '100%', maxWidth: '420px', boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
};

const AdminPortal: React.FC<AdminPortalProps> = ({ token, onLogout }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tenants, setTenants] = useState<any[]>([]);
  const [stats, setStats] = useState({ totalTenants: 0, activeTenants: 0, monthlyRevenue: 0, totalJobs: 0, totalUsers: 0, adminCount: 0 });
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [planModal, setPlanModal] = useState<{ show: boolean; userId: string; currentPlan: string; email: string }>({ show: false, userId: '', currentPlan: '', email: '' });
  const [newPlan, setNewPlan] = useState('standard');
  const [planLoading, setPlanLoading] = useState(false);
  const [planMsg, setPlanMsg] = useState('');

  const [extendModal, setExtendModal] = useState<{ show: boolean; userId: string; email: string }>({ show: false, userId: '', email: '' });
  const [extendDays, setExtendDays] = useState(14);
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendMsg, setExtendMsg] = useState('');

  const [actionMsg, setActionMsg] = useState<{ userId: string; text: string; type: 'success' | 'error' } | null>(null);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, subsRes] = await Promise.all([
        fetch('/api/admin/tenants', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/admin/subscriptions', { headers: { Authorization: `Bearer ${token}` } })
      ]);

      let tenantsData: any[] = [];
      if (tenantsRes.ok) tenantsData = await tenantsRes.json();
      if (subsRes.ok) setSubscriptions(await subsRes.json());

      setTenants(tenantsData);

      setStats({
        totalTenants: tenantsData.length,
        activeTenants: tenantsData.filter((t: any) => t.status === 'active').length,
        monthlyRevenue: 0,
        totalJobs: 0,
        totalUsers: tenantsData.length,
        adminCount: tenantsData.filter((u: any) => u.email === 'admin@locktrak.co.uk').length
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAdminData(); }, []);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  // ── Admin Actions ──────────────────────────────────────────────────────────

  const handleChangePlan = async () => {
    setPlanLoading(true);
    setPlanMsg('');
    try {
      const res = await fetch(`/api/admin/subscriptions/${planModal.userId}/change-plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_type: newPlan })
      });
      const data = await res.json();
      if (res.ok) {
        setPlanMsg(`Plan changed to ${newPlan}`);
        setActionMsg({ userId: planModal.userId, text: `Plan → ${newPlan}`, type: 'success' });
        setTimeout(() => { setPlanModal({ show: false, userId: '', currentPlan: '', email: '' }); loadAdminData(); }, 1000);
      } else {
        setPlanMsg(data.error || 'Failed to change plan');
      }
    } catch (err) {
      setPlanMsg('Error changing plan');
    } finally {
      setPlanLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    setExtendLoading(true);
    setExtendMsg('');
    try {
      const res = await fetch(`/api/admin/subscriptions/${extendModal.userId}/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ days: extendDays })
      });
      const data = await res.json();
      if (res.ok) {
        setExtendMsg(`Trial extended by ${extendDays} days (new end: ${data.new_trial_end})`);
        setActionMsg({ userId: extendModal.userId, text: `Trial +${extendDays}d`, type: 'success' });
        setTimeout(() => { setExtendModal({ show: false, userId: '', email: '' }); loadAdminData(); }, 1500);
      } else {
        setExtendMsg(data.error || 'Failed to extend trial');
      }
    } catch (err) {
      setExtendMsg('Error extending trial');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleCancelSub = async (userId: string) => {
    if (!window.confirm('Cancel this subscription? The user will lose access.')) return;
    try {
      const res = await fetch(`/api/admin/subscriptions/${userId}/disable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActionMsg({ userId, text: 'Canceled', type: 'success' });
        loadAdminData();
      } else {
        alert('Failed to cancel');
      }
    } catch (err) {
      alert('Error');
    }
  };

  const handleReactivate = async (userId: string) => {
    try {
      const res = await fetch(`/api/admin/subscriptions/${userId}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActionMsg({ userId, text: 'Reactivated (14d trial)', type: 'success' });
        loadAdminData();
      } else {
        alert('Failed to reactivate');
      }
    } catch (err) {
      alert('Error');
    }
  };

  // ── Dashboard ──────────────────────────────────────────────────────────────

  const renderDashboard = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', margin: 0, fontWeight: '700', color: '#0f172a' }}>Admin Dashboard</h1>
          <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0 0' }}>System-wide overview</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <div style={{ ...CARD_STYLE, cursor: 'pointer', borderLeft: '4px solid #3b82f6' }}
             onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)'; }}
             onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
             onClick={() => setActiveTab('tenants')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Total Tenants</div>
            <div style={{ width: '36px', height: '36px', background: '#eff6ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'🏢'}</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{stats.totalTenants}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>{stats.activeTenants} active &middot; {stats.totalTenants - stats.activeTenants} inactive</div>
        </div>

        <div style={{ ...CARD_STYLE, borderLeft: '4px solid #10b981' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Active Tenants</div>
            <div style={{ width: '36px', height: '36px', background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'✅'}</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{stats.activeTenants}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>{stats.totalTenants > 0 ? Math.round((stats.activeTenants / stats.totalTenants) * 100) : 0}% of total</div>
        </div>

        <div style={{ ...CARD_STYLE, borderLeft: '4px solid #8b5cf6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Users</div>
            <div style={{ width: '36px', height: '36px', background: '#f5f3ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'👤'}</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{stats.totalUsers}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>{stats.adminCount} admin &middot; {stats.totalUsers - stats.adminCount} technicians</div>
        </div>

        <div style={{ ...CARD_STYLE, borderLeft: '4px solid #f59e0b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Total Jobs</div>
            <div style={{ width: '36px', height: '36px', background: '#fffbeb', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'📋'}</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>{stats.totalJobs}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>Across all tenants</div>
        </div>
      </div>

      <div style={CARD_STYLE}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#0f172a' }}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab('tenants')} style={{ padding: '10px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#475569', transition: 'all 0.2s' }}>
            {'📋'} Manage Tenants
          </button>
          <button onClick={() => setActiveTab('subscriptions')} style={{ padding: '10px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#475569', transition: 'all 0.2s' }}>
            {'💳'} Subscriptions
          </button>
          <button onClick={() => setActiveTab('system')} style={{ padding: '10px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#475569', transition: 'all 0.2s' }}>
            {'⚙️'} System Settings
          </button>
          <button onClick={loadAdminData} style={{ padding: '10px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#475569', transition: 'all 0.2s' }}>
            {'🔄'} Refresh Data
          </button>
        </div>
      </div>
    </div>
  );

  // ── Tenants ────────────────────────────────────────────────────────────────

  const renderTenants = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ fontSize: '20px', margin: 0, color: '#0f172a' }}>Tenant Management</h2>
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>{tenants.length} tenant(s)</span>
      </div>
      {tenants.length > 0 ? (
        <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t, i) => (
                <tr key={t.id} style={{ borderBottom: i < tenants.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '600' }}>{t.name || t.email.split('@')[0]}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>{t.email}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>{t.company_name || '-'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', background: t.role === 'admin' ? '#eff6ff' : '#f0fdf4', color: t.role === 'admin' ? '#1d4ed8' : '#065f46', fontWeight: '500' }}>
                      {t.role || 'technician'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a' }}>{t.plan || 'N/A'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>{formatDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'🏢'}</div>
          <h3 style={{ color: '#0f172a', margin: '0 0 8px 0' }}>No Tenants Yet</h3>
          <p style={{ color: '#94a3b8', margin: 0 }}>Tenants will appear here once users sign up.</p>
        </div>
      )}
    </div>
  );

  // ── Subscriptions ──────────────────────────────────────────────────────────

  const renderSubscriptions = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ fontSize: '20px', margin: 0, color: '#0f172a' }}>Subscription Management</h2>
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>{subscriptions.length} subscription(s)</span>
      </div>
      {subscriptions.length > 0 ? (
        <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Jobs / Limit</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trial End</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub: any, i: number) => {
                const badge = STATUS_BADGE[sub.status] || STATUS_BADGE.incomplete;
                const usagePct = sub.job_limit > 0 ? Math.round((sub.jobs_used / sub.job_limit) * 100) : 0;
                const usageColor = usagePct >= 90 ? '#dc2626' : usagePct >= 75 ? '#d97706' : '#059669';
                const actionText = actionMsg?.userId === sub.id ? (actionMsg?.text || null) : null;
                return (
                  <tr key={sub.id || i} style={{ borderBottom: i < subscriptions.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a', fontWeight: '500' }}>
                      <div>{sub.first_name ? sub.first_name + ' ' + (sub.last_name || '') : sub.email.split('@')[0]}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{sub.email}{sub.company_name ? ' · ' + sub.company_name : ''}</div>
                      {sub.is_nfr && <span style={{ marginTop: '4px', display: 'inline-block', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: '#f0fdf4', color: '#059669', border: '1px solid #86efac' }}>NFR</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a' }}>{
                          sub.status === 'Free' ? 'Free Account' :
                          sub.status === 'Internal' ? 'Internal Account' :
                          sub.plan_name || sub.plan || 'N/A'
                        }</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                        background: badge.bg, color: badge.color, display: 'inline-block'
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{sub.jobs_used ?? 0} / {sub.job_limit ?? '-'}</span>
                        {sub.job_limit > 0 && (
                          <div style={{ flex: 1, maxWidth: '60px', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(usagePct, 100)}%`, height: '100%', background: usageColor, borderRadius: '3px', transition: 'width 0.3s' }} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#64748b' }}>
                      {formatDate(sub.trial_end)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {actionText ? (
                        <span style={{ fontSize: '12px', color: '#059669', fontWeight: '500' }}>{actionText}</span>
                      ) : sub.email === 'admin@locktrak.co.uk' ? (
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>System account</span>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {sub.status === 'Free' ? (
                            <>
<button onClick={async () => {
                                await fetch('/api/admin/subscriptions/' + sub.id + '/set-nfr', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                                  body: JSON.stringify({ is_nfr: false })
                                });
                                loadAdminData();
                              }} title="Remove free status"
                                style={{ padding: '4px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#dc2626', whiteSpace: 'nowrap' }}>
                                Remove Free
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => {
                                setNewPlan(sub.plan || 'standard');
                                setPlanModal({ show: true, userId: sub.id, currentPlan: sub.plan || 'standard', email: sub.email });
                              }} title="Change plan"
                                style={{ padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                                Change Plan
                              </button>
                              {sub.status === 'trial' && (
                                <button onClick={() => setExtendModal({ show: true, userId: sub.id, email: sub.email })} title="Extend trial"
                                  style={{ padding: '4px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#92400e', whiteSpace: 'nowrap' }}>
                                  Extend Trial
                                </button>
                              )}
                              {(sub.status === 'active' || sub.status === 'trial') && sub.status !== 'Free' ? (
                                <button onClick={() => handleCancelSub(sub.id)} title="Cancel"
                                  style={{ padding: '4px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#dc2626', whiteSpace: 'nowrap' }}>
                                  Cancel
                                </button>
                              ) : null}
                              {sub.status === 'canceled' && (
                                <button onClick={() => handleReactivate(sub.id)} title="Reactivate"
                                  style={{ padding: '4px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#065f46', whiteSpace: 'nowrap' }}>
                                  Reactivate
                                </button>
                              )}
                              <button onClick={async () => {
                                const newNfr = !sub.is_nfr;
                                if (!window.confirm(newNfr ? 'Set as free?' : 'Remove free?')) return;
                                await fetch('/api/admin/subscriptions/' + sub.id + '/set-nfr', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                                  body: JSON.stringify({ is_nfr: newNfr })
                                });
                                loadAdminData();
                              }} title={sub.is_nfr ? 'Free active' : 'Set as free'}
                                style={{ padding: '4px 10px', background: sub.is_nfr ? '#f0fdf4' : '#f8fafc', border: '1px solid ' + (sub.is_nfr ? '#86efac' : '#e2e8f0'), borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: sub.is_nfr ? '#059669' : '#64748b', whiteSpace: 'nowrap' }}>
                                {sub.is_nfr ? 'Free' : 'Set Free'}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'💳'}</div>
          <h3 style={{ color: '#0f172a', margin: '0 0 8px 0' }}>No Subscriptions Yet</h3>
          <p style={{ color: '#94a3b8', margin: 0 }}>Subscription data will appear here once tenants have plans assigned.</p>
        </div>
      )}

      {/* Change Plan Modal */}
      {planModal.show && (
        <div style={MODAL_OVERLAY} onClick={() => setPlanModal({ show: false, userId: '', currentPlan: '', email: '' })}>
          <div style={MODAL_CARD} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Change Plan</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px 0' }}>{planModal.email}</p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>New Plan</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {['standard'].map(p => (
                <button key={p} onClick={() => setNewPlan(p)}
                  style={{
                    flex: 1, padding: '12px 8px', borderRadius: '10px', cursor: 'pointer',
                    border: newPlan === p ? '2px solid #1e3c72' : '1px solid #e2e8f0',
                    background: newPlan === p ? '#eff6ff' : 'white',
                    textAlign: 'center', transition: 'all 0.2s'
                  }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>Standard</div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>£6/month</div>
                </button>
              ))}
            </div>

            {planMsg && <div style={{ padding: '10px', borderRadius: '8px', background: planMsg.includes('changed') ? '#d1fae5' : '#fee2e2', color: planMsg.includes('changed') ? '#065f46' : '#991b1b', fontSize: '13px', marginBottom: '12px' }}>{planMsg}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleChangePlan} disabled={planLoading}
                style={{ flex: 1, padding: '10px', background: '#1e3c72', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                {planLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setPlanModal({ show: false, userId: '', currentPlan: '', email: '' })}
                style={{ padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Trial Modal */}
      {extendModal.show && (
        <div style={MODAL_OVERLAY} onClick={() => setExtendModal({ show: false, userId: '', email: '' })}>
          <div style={MODAL_CARD} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Extend Trial</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px 0' }}>{extendModal.email}</p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Extra Days</label>
            <input type="number" value={extendDays} onChange={e => setExtendDays(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[7, 14, 30, 60].map(d => (
                <button key={d} onClick={() => setExtendDays(d)}
                  style={{ padding: '6px 14px', borderRadius: '8px', border: extendDays === d ? '2px solid #1e3c72' : '1px solid #e2e8f0', background: extendDays === d ? '#eff6ff' : 'white', cursor: 'pointer', fontSize: '12px', color: '#475569' }}>
                  {d} days
                </button>
              ))}
            </div>

            {extendMsg && <div style={{ padding: '10px', borderRadius: '8px', background: extendMsg.includes('extended') ? '#d1fae5' : '#fee2e2', color: extendMsg.includes('extended') ? '#065f46' : '#991b1b', fontSize: '13px', marginBottom: '12px' }}>{extendMsg}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleExtendTrial} disabled={extendLoading}
                style={{ flex: 1, padding: '10px', background: '#d97706', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                {extendLoading ? 'Extending...' : `Extend by ${extendDays} days`}
              </button>
              <button onClick={() => setExtendModal({ show: false, userId: '', email: '' })}
                style={{ padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3c72 50%, #2a5298 100%)',
        color: 'white', padding: '14px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '38px', height: '38px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', backdropFilter: 'blur(10px)' }}>{'🔐'}</div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 'bold', letterSpacing: '-0.3px' }}>Lock<span style={{ color: '#60a5fa' }}>Trak</span> <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.7, marginLeft: '6px' }}>Admin Console</span></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={loadAdminData} style={{
            padding: '7px 14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
            color: 'white', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
          }}>{'🔄'} Refresh</button>
          <button onClick={() => setShowSettings(true)} style={{
            padding: '7px 14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
            color: 'white', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s', backdropFilter: 'blur(10px)'
          }}>{'👤'} Profile</button>
          <button onClick={onLogout} style={{
            padding: '7px 14px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px',
            color: '#fca5a5', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s'
          }}>Logout</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: 'white', padding: '10px 24px', display: 'flex', gap: '6px', overflowX: 'auto', borderBottom: '1px solid #e2e8f0' }}>
        <button key="dashboard" onClick={() => setActiveTab('dashboard')} style={TAB_STYLE(activeTab === 'dashboard')}>{'📊'} Dashboard</button>
        <button key="tenants" onClick={() => setActiveTab('tenants')} style={TAB_STYLE(activeTab === 'tenants')}>{'🏢'} Tenants</button>
        <button key="subscriptions" onClick={() => setActiveTab('subscriptions')} style={TAB_STYLE(activeTab === 'subscriptions')}>{'💳'} Subscriptions</button>
        <button key="system" onClick={() => setActiveTab('system')} style={TAB_STYLE(activeTab === 'system')}>{'⚙️'} System</button>
        <button key="security" onClick={() => setActiveTab('security')} style={TAB_STYLE(activeTab === 'security')}>{'🛡️'} Security</button>
        <button key="audit" onClick={() => setActiveTab('audit')} style={TAB_STYLE(activeTab === 'audit')}>{'📋'} Audit Log</button>
        <button key="announcements" onClick={() => setActiveTab('announcements')} style={TAB_STYLE(activeTab === 'announcements')}>{'📢'} Announcements</button>
        <button key="tickets" onClick={() => setActiveTab('tickets')} style={TAB_STYLE(activeTab === 'tickets')}>{'🎫'} Tickets</button>
        <button key="knowledge" onClick={() => setActiveTab('knowledge')} style={TAB_STYLE(activeTab === 'knowledge')}>{'📚'} Knowledge Base</button>
      </div>

      {/* Content */}
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'tenants' && renderTenants()}
        {activeTab === 'subscriptions' && renderSubscriptions()}
        {activeTab === 'audit' && <AuditLog token={token} />}
        {activeTab === 'security' && <SecurityDashboard token={token} />}
        {activeTab === 'announcements' && <AnnouncementsManagement token={token} />}
        {activeTab === 'tickets' && <SupportTickets token={token} adminMode={true} />}
        {activeTab === 'system' && <SystemManagement token={token} />}
        {activeTab === 'knowledge' && <KnowledgeBase token={token} />}
      </div>
      {showSettings && (
        <SettingsModal token={token} onClose={() => setShowSettings(false)} onProfileUpdated={() => {}} />
      )}
    </div>
  );
};

export default AdminPortal;
